import { useDispatch, useStore } from 'react-redux';
import { useRef, useState, type ReactNode } from 'react';
import { normalizeColumnWidthInput } from '@worldathletics/utilities';

import { setColumnAlign, setColumnWidth } from '@/lib/table-ops';
import type { RootState } from '@/context/store/store';
import {
  updateNode,
  type DocumentEntity,
} from '@/context/store/slices/documentDataSlice';

type TableNode = DocumentEntity & { type: 'table' };

interface Props {
  node: TableNode;
  columnIndex: number;
  onClose: () => void;
}

const stroke = 'currentColor';

function AlignLeft() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path
        d="M2 3.5h12M2 7h8M2 10.5h12M2 14h8"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AlignCenter() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path
        d="M2 3.5h12M4 7h8M2 10.5h12M4 14h8"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AlignRight() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
      <path
        d="M2 3.5h12M6 7h8M2 10.5h12M6 14h8"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" fill="none">
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ApplyIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" fill="none">
      <path
        d="M3.5 8.5 6.5 11.5 12.5 4.5"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ALIGN_BUTTONS: {
  value: 'left' | 'center' | 'right';
  label: string;
  Icon: () => ReactNode;
}[] = [
  { value: 'left', label: 'Align left', Icon: AlignLeft },
  { value: 'center', label: 'Align center', Icon: AlignCenter },
  { value: 'right', label: 'Align right', Icon: AlignRight },
];

const WIDTH_PRESETS = ['auto', '25%', '50%', '120px'];

export function ColumnPopover({ node, columnIndex, onClose }: Props) {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const current = node.columns?.[columnIndex] ?? {};
  const committedWidth = current.width ?? '';
  const [draftWidth, setDraftWidth] = useState(committedWidth);
  const inputFocusedRef = useRef(false);

  function latestTableNode(): TableNode {
    const fromStore = store.getState().documentData.byId[node.id];
    return (fromStore ?? node) as TableNode;
  }

  function applyAlign(align: 'left' | 'center' | 'right'): void {
    const latest = latestTableNode();
    const next = setColumnAlign(latest, columnIndex, align);
    dispatch(updateNode({ id: node.id, changes: { columns: next.columns } }));
  }

  function applyWidth(width: string): void {
    const latest = latestTableNode();
    const next = setColumnWidth(latest, columnIndex, width);
    dispatch(updateNode({ id: node.id, changes: { columns: next.columns } }));
  }

  function commitWidth(raw: string): void {
    const normalized = normalizeColumnWidthInput(raw);
    applyWidth(normalized);
    setDraftWidth(normalized);
  }

  function applyPreset(preset: string): void {
    const value = preset === 'auto' ? '' : preset;
    applyWidth(value);
    setDraftWidth(value);
  }

  return (
    <div role="dialog" className="column-popover" aria-label="Column properties">
      <div className="column-popover__header">
        <span className="column-popover__eyebrow">Column</span>
        <span className="column-popover__index">
          {String(columnIndex + 1).padStart(2, '0')}
        </span>
        <button
          type="button"
          className="column-popover__close"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="column-popover__section">
        <span className="column-popover__label">Alignment</span>
        <div
          className="column-popover__segmented"
          role="radiogroup"
          aria-label="Alignment"
        >
          {ALIGN_BUTTONS.map(({ value, label, Icon }) => {
            const active = current.align === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={label}
                title={label}
                className={`column-popover__seg-btn${active ? ' is-active' : ''}`}
                onClick={() => applyAlign(value)}
              >
                <Icon />
              </button>
            );
          })}
        </div>
      </div>

      <div className="column-popover__section">
        <span className="column-popover__label">Width</span>
        <div className="column-popover__width">
          <div className="column-popover__width-field">
            <input
              className="column-popover__input"
              type="text"
              aria-label="Width"
              value={draftWidth}
              placeholder="auto"
              onChange={e => setDraftWidth(e.target.value)}
              onFocus={() => {
                inputFocusedRef.current = true;
              }}
              onBlur={() => {
                inputFocusedRef.current = false;
                commitWidth(draftWidth);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitWidth(draftWidth);
                  e.currentTarget.blur();
                }
              }}
            />
            <button
              type="button"
              className="column-popover__apply"
              aria-label="Apply width"
              title="Apply width"
              onClick={() => commitWidth(draftWidth)}
            >
              <ApplyIcon />
            </button>
          </div>
          <div className="column-popover__presets">
            {WIDTH_PRESETS.map(preset => {
              const active =
                (current.width ?? '') === preset ||
                (preset === 'auto' && !current.width);
              return (
                <button
                  key={preset}
                  type="button"
                  className={`column-popover__chip${active ? ' is-active' : ''}`}
                  onClick={() => applyPreset(preset)}
                >
                  {preset}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
